import { useEffect, useMemo, useState } from "react";
import {
  applyScopedHierarchyFilterLists,
  buildUniqueCircles,
  buildUniqueDivisions,
  buildUniqueSubDivisions,
  getAvailableReportLevelOptions,
  getDefaultReportLevel,
  getEffectiveUserHierarchy,
  getHierarchyFilterEnabledState,
  isHierarchicalReportLevelValue,
  resolveApmsReportOfficeCode,
  resolveAuthOfficeCode,
} from "../utils/technicalReportLevel";

export default function useTechnicalReportLevel({
  code,
  userHierarchy,
  cfcLevelId,
  hierarchy,
  offCode,
  subDivisions,
  selectedCompanyCode,
  selectedAdminCompanyCode,
  officeCode,
  circleCode,
  selectedDivision,
  selectedSubDivision,
  setCircleCode,
  setCircleName,
  setSelectedDivision,
  setSelectedSubDivision,
  setFilteredCircles,
  setFilteredDivisions,
  setFilteredSubDivisions,
  setOfficeCode,
  setOfficeName,
  getCompanies,
  getName,
}) {
  const effectiveHierarchy = useMemo(
    () => getEffectiveUserHierarchy(hierarchy, cfcLevelId, userHierarchy, code),
    [hierarchy, cfcLevelId, userHierarchy, code],
  );

  const [reportLevel, setReportLevel] = useState(() =>
    getDefaultReportLevel(code, effectiveHierarchy),
  );
  const [appliedReportLevel, setAppliedReportLevel] = useState(() =>
    getDefaultReportLevel(code, effectiveHierarchy),
  );

  const isAdminUser = String(code) === "10";
  const availableReportLevelOptions = useMemo(
    () => getAvailableReportLevelOptions(code, effectiveHierarchy),
    [code, effectiveHierarchy],
  );
  const authOfficeCode = resolveAuthOfficeCode(code, effectiveHierarchy);

  const {
    showCircleFilter,
    showDivisionFilter,
    showSubDivisionFilter,
    circleFilterEnabled,
    divisionFilterEnabled,
    subDivisionFilterEnabled,
  } = getHierarchyFilterEnabledState({
    reportLevel,
    code,
    effectiveHierarchy,
    circleCode,
    selectedDivision,
  });

  useEffect(() => {
    if (!availableReportLevelOptions.length) {
      return;
    }

    const isCurrentLevelAllowed = availableReportLevelOptions.some(
      (option) => option.value === reportLevel,
    );

    if (!isCurrentLevelAllowed) {
      const nextLevel =
        availableReportLevelOptions[0]?.value ||
        getDefaultReportLevel(code, effectiveHierarchy);
      setReportLevel(nextLevel);
    }
  }, [availableReportLevelOptions, effectiveHierarchy, code, reportLevel]);

  const resetHierarchyFiltersToAuthScope = () => {
    if (effectiveHierarchy >= 5) {
      setSelectedSubDivision("");
    } else if (effectiveHierarchy === 4) {
      setSelectedSubDivision("");
    } else if (effectiveHierarchy === 3) {
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
    } else {
      setCircleCode("");
      setCircleName("");
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
    }
  };

  const restoreHierarchyScopedFilters = () => {
    if (effectiveHierarchy >= 3) {
      applyScopedHierarchyFilterLists({
        items: subDivisions,
        effectiveHierarchy,
        authCode: offCode,
        setCircleCode,
        setSelectedDivision,
        setSelectedSubDivision,
        setFilteredCircles,
        setFilteredDivisions,
        setFilteredSubDivisions,
      });
    }
  };

  const handleReportLevelChange = (event) => {
    const nextLevel = event.target.value;
    const level = Number(nextLevel);
    setReportLevel(nextLevel);

    if (isAdminUser && level !== 6) {
      setCircleCode("");
      setCircleName("");
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
      const company =
        selectedCompanyCode || String(selectedAdminCompanyCode) || "10";
      setOfficeCode(company);
      setOfficeName(company === "10" ? "PITC" : getCompanies(company) || "");
      setFilteredDivisions(buildUniqueDivisions(subDivisions));
      setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      return;
    }

    if (!isAdminUser && level !== 6) {
      resetHierarchyFiltersToAuthScope();
      setOfficeCode(authOfficeCode);
      setOfficeName(getName(authOfficeCode, subDivisions) || "");
      return;
    }

    if (isAdminUser && level === 6 && subDivisions.length > 0) {
      setFilteredCircles(buildUniqueCircles(subDivisions));
      setFilteredDivisions(buildUniqueDivisions(subDivisions));
      setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      return;
    }

    if (!isAdminUser && level === 6) {
      resetHierarchyFiltersToAuthScope();
      restoreHierarchyScopedFilters();
      setOfficeCode(authOfficeCode);
      setOfficeName(getName(authOfficeCode, subDivisions) || "");

      if (effectiveHierarchy <= 2 && subDivisions.length > 0) {
        setFilteredCircles(buildUniqueCircles(subDivisions));
        setFilteredDivisions(buildUniqueDivisions(subDivisions));
        setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      }
    }
  };

  useEffect(() => {
    if (isAdminUser || !isHierarchicalReportLevelValue(reportLevel)) {
      return;
    }

    if (effectiveHierarchy >= 3) {
      applyScopedHierarchyFilterLists({
        items: subDivisions,
        effectiveHierarchy,
        authCode: offCode,
        setCircleCode,
        setSelectedDivision,
        setSelectedSubDivision,
        setFilteredCircles,
        setFilteredDivisions,
        setFilteredSubDivisions,
      });
    }
  }, [
    reportLevel,
    effectiveHierarchy,
    subDivisions,
    offCode,
    isAdminUser,
    setCircleCode,
    setSelectedDivision,
    setSelectedSubDivision,
    setFilteredCircles,
    setFilteredDivisions,
    setFilteredSubDivisions,
  ]);

  const resolveApiOfficeCode = () =>
    resolveApmsReportOfficeCode({
      userCode: code,
      effectiveHierarchy,
      reportLevelValue: reportLevel,
      officeCode,
      selectedCompanyCode,
      selectedAdminCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });

  return {
    reportLevel,
    setReportLevel,
    appliedReportLevel,
    setAppliedReportLevel,
    availableReportLevelOptions,
    handleReportLevelChange,
    showCircleFilter,
    showDivisionFilter,
    showSubDivisionFilter,
    circleFilterEnabled,
    divisionFilterEnabled,
    subDivisionFilterEnabled,
    effectiveHierarchy,
    resolveApiOfficeCode,
    isHierarchicalReportLevel: isHierarchicalReportLevelValue(reportLevel),
  };
}
